import LineString from 'ol/geom/LineString';
import MultiPolygon from 'ol/geom/MultiPolygon';
import Polygon from 'ol/geom/Polygon';
import type Geometry from 'ol/geom/Geometry';
import polygonClipping, { type MultiPolygon as ClippingMultiPolygon, type Polygon as ClippingPolygon } from 'polygon-clipping';
import { measureArea } from './measurement';

type XY = [number, number];

interface RingLocation {
  readonly segmentIndex: number;
  readonly fraction: number;
  readonly coordinate: XY;
}

interface SegmentHit {
  readonly kind: 'point' | 'overlap';
  readonly coordinate?: XY;
}

export interface BoundaryCutPlan {
  readonly componentIndex: number;
  readonly snappedLine: LineString;
  readonly boundaryPaths: readonly [readonly XY[], readonly XY[]];
  readonly candidates: readonly [Polygon, Polygon];
  readonly candidateAreaHa: readonly [number, number];
}

export const BOUNDARY_CUT_COMPLEX_MESSAGE = 'Este corte es demasiado complejo para Corte al borde. Usa Área libre.';
const FREE_CUT_SELF_INTERSECTION_MESSAGE = 'El área dibujada se cruza consigo misma. Corrige el trazado o utiliza Corte al borde.';
const COORDINATE_EPSILON = 1e-6;
const AREA_EPSILON_M2 = 0.0001;
const { intersection } = polygonClipping;

const asXY = (coordinate: readonly number[]): XY => [coordinate[0]!, coordinate[1]!];
const finiteCoordinate = (coordinate: readonly number[]): boolean => coordinate.length >= 2 && Number.isFinite(coordinate[0]) && Number.isFinite(coordinate[1]);
const distanceSquared = (left: readonly number[], right: readonly number[]): number => (left[0]! - right[0]!) ** 2 + (left[1]! - right[1]!) ** 2;
const sameCoordinate = (left: readonly number[], right: readonly number[], epsilon = COORDINATE_EPSILON): boolean => distanceSquared(left, right) <= epsilon ** 2;
const cross = (ax: number, ay: number, bx: number, by: number): number => ax * by - ay * bx;

const segmentHit = (a: XY, b: XY, c: XY, d: XY): SegmentHit | null => {
  const rx = b[0] - a[0]; const ry = b[1] - a[1];
  const sx = d[0] - c[0]; const sy = d[1] - c[1];
  const qx = c[0] - a[0]; const qy = c[1] - a[1];
  const denominator = cross(rx, ry, sx, sy);
  const scale = Math.max(1, Math.hypot(rx, ry), Math.hypot(sx, sy));
  const crossEpsilon = COORDINATE_EPSILON * scale;
  if (Math.abs(denominator) <= crossEpsilon) {
    if (Math.abs(cross(qx, qy, rx, ry)) > crossEpsilon) return null;
    const lengthSquared = rx * rx + ry * ry;
    if (lengthSquared <= COORDINATE_EPSILON ** 2) return sameCoordinate(a, c) ? { kind: 'point', coordinate: [...a] } : null;
    const t0 = (qx * rx + qy * ry) / lengthSquared;
    const t1 = t0 + (sx * rx + sy * ry) / lengthSquared;
    const start = Math.max(0, Math.min(t0, t1));
    const end = Math.min(1, Math.max(t0, t1));
    if (end < start - COORDINATE_EPSILON) return null;
    if (Math.abs(end - start) <= COORDINATE_EPSILON) return { kind: 'point', coordinate: [a[0] + start * rx, a[1] + start * ry] };
    return { kind: 'overlap' };
  }
  const t = cross(qx, qy, sx, sy) / denominator;
  const u = cross(qx, qy, rx, ry) / denominator;
  if (t < -COORDINATE_EPSILON || t > 1 + COORDINATE_EPSILON || u < -COORDINATE_EPSILON || u > 1 + COORDINATE_EPSILON) return null;
  return { kind: 'point', coordinate: [a[0] + t * rx, a[1] + t * ry] };
};

const projectToSegment = (coordinate: XY, start: XY, end: XY): { coordinate: XY; fraction: number; distanceSquared: number } => {
  const dx = end[0] - start[0]; const dy = end[1] - start[1];
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared <= COORDINATE_EPSILON ** 2) return { coordinate: [...start], fraction: 0, distanceSquared: distanceSquared(coordinate, start) };
  const fraction = Math.max(0, Math.min(1, ((coordinate[0] - start[0]) * dx + (coordinate[1] - start[1]) * dy) / lengthSquared));
  const projected: XY = [start[0] + fraction * dx, start[1] + fraction * dy];
  return { coordinate: projected, fraction, distanceSquared: distanceSquared(coordinate, projected) };
};

const openRing = (ring: readonly (readonly number[])[]): XY[] => {
  const coordinates = ring.map(asXY);
  return coordinates.length > 1 && sameCoordinate(coordinates[0]!, coordinates.at(-1)!) ? coordinates.slice(0, -1) : coordinates;
};

const locateOnRing = (ring: readonly (readonly number[])[], coordinate: XY): RingLocation | null => {
  const vertices = openRing(ring);
  if (vertices.length < 3) return null;
  let best: { segmentIndex: number; fraction: number; coordinate: XY; distanceSquared: number } | null = null;
  for (let segmentIndex = 0; segmentIndex < vertices.length; segmentIndex += 1) {
    const start = vertices[segmentIndex]!;
    const projected = projectToSegment(coordinate, start, vertices[(segmentIndex + 1) % vertices.length]!);
    if (!best || projected.distanceSquared < best.distanceSquared) best = { segmentIndex, ...projected };
  }
  if (!best || best.distanceSquared > COORDINATE_EPSILON ** 2) return null;
  if (best.fraction >= 1 - COORDINATE_EPSILON) {
    const next = (best.segmentIndex + 1) % vertices.length;
    return { segmentIndex: next, fraction: 0, coordinate: [...vertices[next]!] };
  }
  if (best.fraction <= COORDINATE_EPSILON) return { segmentIndex: best.segmentIndex, fraction: 0, coordinate: [...vertices[best.segmentIndex]!] };
  return { segmentIndex: best.segmentIndex, fraction: best.fraction, coordinate: best.coordinate };
};

const pushUnique = (coordinates: XY[], coordinate: XY): void => {
  if (!coordinates.length || !sameCoordinate(coordinates.at(-1)!, coordinate)) coordinates.push([...coordinate]);
};

const forwardBoundaryPath = (ring: readonly (readonly number[])[], start: RingLocation, end: RingLocation): XY[] => {
  const vertices = openRing(ring);
  const path: XY[] = [[...start.coordinate]];
  if (start.segmentIndex === end.segmentIndex && end.fraction > start.fraction + COORDINATE_EPSILON) {
    pushUnique(path, end.coordinate);
    return path;
  }
  let segmentIndex = start.segmentIndex;
  for (let guard = 0; guard <= vertices.length; guard += 1) {
    pushUnique(path, vertices[(segmentIndex + 1) % vertices.length]!);
    segmentIndex = (segmentIndex + 1) % vertices.length;
    if (segmentIndex === end.segmentIndex) {
      pushUnique(path, end.coordinate);
      return path;
    }
  }
  throw new Error(BOUNDARY_CUT_COMPLEX_MESSAGE);
};

const closedCandidateRing = (lineCoordinates: readonly XY[], boundaryAtoB: readonly XY[]): XY[] => {
  const ring = lineCoordinates.map((coordinate) => [...coordinate] as XY);
  [...boundaryAtoB].reverse().slice(1).forEach((coordinate) => pushUnique(ring, coordinate));
  pushUnique(ring, ring[0]!);
  return ring;
};

const asClipping = (geometry: Polygon): ClippingPolygon => geometry.getCoordinates() as ClippingPolygon;
const fromClippingCandidate = (coordinates: ClippingMultiPolygon): Polygon => {
  if (coordinates.length !== 1) throw new Error(BOUNDARY_CUT_COMPLEX_MESSAGE);
  const polygon = new Polygon(coordinates[0]!);
  if (!Number.isFinite(measureArea(polygon).areaM2) || measureArea(polygon).areaM2 <= AREA_EPSILON_M2) throw new Error(BOUNDARY_CUT_COMPLEX_MESSAGE);
  return polygon;
};

const validateLineSimplicity = (coordinates: readonly XY[]): void => {
  if (coordinates.length < 2 || coordinates.some((coordinate) => !finiteCoordinate(coordinate))) throw new Error(BOUNDARY_CUT_COMPLEX_MESSAGE);
  for (let index = 0; index < coordinates.length - 1; index += 1) {
    if (sameCoordinate(coordinates[index]!, coordinates[index + 1]!)) throw new Error(BOUNDARY_CUT_COMPLEX_MESSAGE);
    for (let other = index + 2; other < coordinates.length - 1; other += 1) {
      if (segmentHit(coordinates[index]!, coordinates[index + 1]!, coordinates[other]!, coordinates[other + 1]!)) throw new Error(BOUNDARY_CUT_COMPLEX_MESSAGE);
    }
  }
};

const validateLineAgainstRing = (line: readonly XY[], ring: readonly (readonly number[])[], start: XY, end: XY): void => {
  const vertices = openRing(ring);
  for (let lineIndex = 0; lineIndex < line.length - 1; lineIndex += 1) {
    for (let ringIndex = 0; ringIndex < vertices.length; ringIndex += 1) {
      const hit = segmentHit(line[lineIndex]!, line[lineIndex + 1]!, vertices[ringIndex]!, vertices[(ringIndex + 1) % vertices.length]!);
      if (!hit) continue;
      if (hit.kind === 'overlap') throw new Error(BOUNDARY_CUT_COMPLEX_MESSAGE);
      const allowedStart = lineIndex === 0 && hit.coordinate && sameCoordinate(hit.coordinate, start);
      const allowedEnd = lineIndex === line.length - 2 && hit.coordinate && sameCoordinate(hit.coordinate, end);
      if (!allowedStart && !allowedEnd) throw new Error(BOUNDARY_CUT_COMPLEX_MESSAGE);
    }
  }
};

const validateInteriorLine = (line: readonly XY[], polygon: Polygon): void => {
  const rings = polygon.getCoordinates();
  validateLineAgainstRing(line, rings[0]!, line[0]!, line.at(-1)!);
  rings.slice(1).forEach((hole) => {
    const vertices = openRing(hole);
    for (let lineIndex = 0; lineIndex < line.length - 1; lineIndex += 1) {
      for (let ringIndex = 0; ringIndex < vertices.length; ringIndex += 1) {
        if (segmentHit(line[lineIndex]!, line[lineIndex + 1]!, vertices[ringIndex]!, vertices[(ringIndex + 1) % vertices.length]!)) throw new Error(BOUNDARY_CUT_COMPLEX_MESSAGE);
      }
    }
  });
  for (let index = 0; index < line.length - 1; index += 1) {
    const start = line[index]!; const end = line[index + 1]!;
    for (const fraction of [0.25, 0.5, 0.75]) {
      const sample: XY = [start[0] + (end[0] - start[0]) * fraction, start[1] + (end[1] - start[1]) * fraction];
      if (!polygon.intersectsCoordinate(sample)) throw new Error(BOUNDARY_CUT_COMPLEX_MESSAGE);
    }
  }
};

const polygonsOf = (geometry: Polygon | MultiPolygon): Polygon[] => geometry instanceof Polygon ? [geometry] : geometry.getPolygons();

export function buildBoundaryCutPlan(activeGeometry: Geometry, drawnLine: Geometry): BoundaryCutPlan {
  if (!(activeGeometry instanceof Polygon || activeGeometry instanceof MultiPolygon) || !(drawnLine instanceof LineString)) throw new Error(BOUNDARY_CUT_COMPLEX_MESSAGE);
  const originalLine = drawnLine.getCoordinates().map(asXY);
  if (originalLine.length < 2 || originalLine.some((coordinate) => !finiteCoordinate(coordinate))) throw new Error(BOUNDARY_CUT_COMPLEX_MESSAGE);
  const polygons = polygonsOf(activeGeometry);
  const startMatches = polygons.flatMap((polygon, componentIndex) => {
    const location = locateOnRing(polygon.getCoordinates()[0]!, originalLine[0]!);
    return location ? [{ componentIndex, location }] : [];
  });
  if (!startMatches.length) throw new Error('El inicio debe quedar ajustado al borde del fragmento activo.');
  const endMatches = polygons.flatMap((polygon, componentIndex) => {
    const location = locateOnRing(polygon.getCoordinates()[0]!, originalLine.at(-1)!);
    return location ? [{ componentIndex, location }] : [];
  });
  if (!endMatches.length) throw new Error('El final debe quedar ajustado al borde del fragmento activo.');
  const common = startMatches.flatMap((start) => endMatches.filter((end) => end.componentIndex === start.componentIndex).map((end) => ({ componentIndex: start.componentIndex, start: start.location, end: end.location })));
  if (common.length !== 1) throw new Error(BOUNDARY_CUT_COMPLEX_MESSAGE);
  const { componentIndex, start, end } = common[0]!;
  if (sameCoordinate(start.coordinate, end.coordinate)) throw new Error(BOUNDARY_CUT_COMPLEX_MESSAGE);
  const component = polygons[componentIndex]!;
  const lineCoordinates: XY[] = [[...start.coordinate], ...originalLine.slice(1, -1).map((coordinate) => [...coordinate] as XY), [...end.coordinate]];
  validateLineSimplicity(lineCoordinates);
  validateInteriorLine(lineCoordinates, component);
  const exterior = component.getCoordinates()[0]!;
  const forward = forwardBoundaryPath(exterior, start, end);
  const backward = [...forwardBoundaryPath(exterior, end, start)].reverse();
  const rawCandidates = [forward, backward].map((path) => new Polygon([closedCandidateRing(lineCoordinates, path)]));
  const candidates = rawCandidates.map((candidate) => fromClippingCandidate(intersection(asClipping(component), asClipping(candidate)))) as [Polygon, Polygon];
  const areas = candidates.map((candidate) => measureArea(candidate).areaM2) as [number, number];
  const componentArea = measureArea(component).areaM2;
  const tolerance = Math.max(0.01, componentArea * 1e-8);
  if (Math.abs(areas[0] + areas[1] - componentArea) > tolerance) throw new Error(BOUNDARY_CUT_COMPLEX_MESSAGE);
  return {
    componentIndex,
    snappedLine: new LineString(lineCoordinates),
    boundaryPaths: [forward, backward],
    candidates,
    candidateAreaHa: [areas[0] / 10_000, areas[1] / 10_000],
  };
}

export function validateFreeDifferencePolygon(geometry: Geometry): asserts geometry is Polygon {
  if (!(geometry instanceof Polygon)) throw new Error('Dibuja un polígono válido para quitar área.');
  const rings = geometry.getCoordinates();
  if (!rings.length) throw new Error('El área dibujada no contiene un anillo válido.');
  for (const ring of rings) {
    if (ring.length < 4 || ring.some((coordinate) => !finiteCoordinate(coordinate)) || !sameCoordinate(ring[0]!, ring.at(-1)!)) throw new Error('El área dibujada contiene coordenadas inválidas o un anillo degenerado.');
    const vertices = ring.slice(0, -1).map(asXY);
    if (new Set(vertices.map(([x, y]) => `${x}:${y}`)).size < 3) throw new Error('El área dibujada contiene coordenadas inválidas o un anillo degenerado.');
    for (let index = 0; index < vertices.length; index += 1) {
      const next = (index + 1) % vertices.length;
      if (sameCoordinate(vertices[index]!, vertices[next]!)) throw new Error('El área dibujada contiene coordenadas inválidas o un anillo degenerado.');
      for (let other = index + 1; other < vertices.length; other += 1) {
        const otherNext = (other + 1) % vertices.length;
        const adjacent = other === index || other === next || otherNext === index;
        if (!adjacent && segmentHit(vertices[index]!, vertices[next]!, vertices[other]!, vertices[otherNext]!)) throw new Error(FREE_CUT_SELF_INTERSECTION_MESSAGE);
      }
    }
  }
  const area = measureArea(geometry).areaM2;
  if (!Number.isFinite(area) || area <= AREA_EPSILON_M2) throw new Error('El área dibujada contiene coordenadas inválidas o un anillo degenerado.');
}
